import { useProjects, useServices } from "@hq/client-react";
import type { Project } from "@hq/contracts";
import { fuzzyScore, type NewTaskInput, sortProjects } from "@hq/core";
import { cn, Command, Input, Kbd, toast } from "@z/ui";
import { type CSSProperties, type FocusEvent, useId, useState } from "react";
import type { AddTodoRequest } from "./commands";

export const INBOX_VALUE = "inbox";
const PANEL_WIDTH = 320;
const PANEL_MAX_HEIGHT = 360;
const GAP = 6;
const ACTIVE_PROJECTS = { status: ["active"] } as const;

export interface AddTodoPopoverProps {
  request: AddTodoRequest;
  sourceNoteId?: string | undefined;
  /** Called once the popover is done (task created, Escape, or focus left it). */
  onClose: () => void;
  className?: string;
}

/** Active projects matching `query`, best fuzzy match first; project order when `query` is empty. */
export function matchProjects(projects: readonly Project[], query: string): Project[] {
  const q = query.trim();
  const sorted = sortProjects(projects);
  if (!q) return sorted;
  return sorted
    .map((project, index) => ({ project, index, score: fuzzyScore(q, project.name) }))
    .filter((m) => m.score >= 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((m) => m.project);
}

/**
 * Places the panel below the cursor, or above it when the bottom of the viewport is too close.
 * `position: fixed` in viewport coordinates matches what `view.coordsAtPos` returns, so no
 * conversion is needed and the editor host's `overflow: hidden` cannot clip it.
 */
function panelStyle(coords: AddTodoRequest["coords"]): CSSProperties {
  if (!coords) return { position: "fixed", left: 16, top: 16, width: PANEL_WIDTH };
  const viewportWidth = typeof window === "undefined" ? Infinity : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? Infinity : window.innerHeight;
  const left = Math.max(8, Math.min(coords.left, viewportWidth - PANEL_WIDTH - 8));
  const fitsBelow = coords.bottom + GAP + PANEL_MAX_HEIGHT <= viewportHeight;
  if (fitsBelow || coords.top < viewportHeight / 2) {
    return { position: "fixed", left, top: coords.bottom + GAP, width: PANEL_WIDTH };
  }
  return {
    position: "fixed",
    left,
    bottom: viewportHeight - coords.top + GAP,
    width: PANEL_WIDTH,
  };
}

/**
 * "Send line to todo". The note text is deliberately left untouched: the task is a copy of the
 * line, not a replacement for it, so the note keeps reading the way it was written. Enter
 * creates the task in the active destination (Inbox unless a project is highlighted), Escape
 * closes; either way `onClose` runs and the host returns focus to the editor.
 */
export function AddTodoPopover({ request, sourceNoteId, onClose, className }: AddTodoPopoverProps) {
  const services = useServices();
  const { data: projects } = useProjects(ACTIVE_PROJECTS);
  const [title, setTitle] = useState(request.title);
  const [query, setQuery] = useState("");
  const titleId = useId();
  const matches = matchProjects(projects ?? [], query);

  const create = (project: Project | undefined) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    onClose();
    const input: NewTaskInput = { title: trimmed };
    if (project) input.projectId = project.id;
    if (sourceNoteId) input.sourceNoteId = sourceNoteId;
    const destination = project ? project.name : sourceNoteId ? "this note" : "Inbox";
    services.tasks
      .create(input)
      .then((task) => {
        toast({
          title: `Added to ${destination}`,
          description: trimmed,
          variant: "success",
          action: { label: "Undo", onClick: () => void services.tasks.delete(task.id) },
        });
      })
      .catch((error: unknown) => {
        console.error("Add todo failed", error);
        toast({ title: "Couldn't add the todo", variant: "danger" });
      });
  };

  const onSelect = (value: string) => {
    create(value === INBOX_VALUE ? undefined : matches.find((p) => p.id === value));
  };

  // Focus leaving the panel entirely (a click elsewhere, Tab past the last control) closes it.
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Add as todo"
      data-add-todo=""
      style={panelStyle(request.coords)}
      onBlur={onBlur}
      className={cn(
        "z-dropdown flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised text-fg shadow-md",
        className,
      )}
    >
      <Command.Root value={query} onValueChange={setQuery} onEscape={onClose} className="h-auto">
        <div className="flex flex-col gap-1 border-b border-border px-3 pt-2.5 pb-2">
          <label htmlFor={titleId} className="text-xs font-medium text-fg-muted">
            Todo
          </label>
          <Input
            id={titleId}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              // Keep Home/End as caret movement inside the title; the list owns the rest.
              if (event.key === "Home" || event.key === "End") event.stopPropagation();
            }}
            className="h-8"
          />
        </div>
        <Command.Input
          placeholder={
            sourceNoteId ? "Add to… (This note or a project)" : "Add to… (Inbox or a project)"
          }
          autoFocus
        />
        <Command.List className="max-h-56">
          {(!query.trim() || fuzzyScore(query, sourceNoteId ? "This note" : "Inbox") >= 0) && (
            <Command.Item value={INBOX_VALUE} onSelect={onSelect}>
              {sourceNoteId ? "This note" : "Inbox"}
            </Command.Item>
          )}
          {matches.map((project) => (
            <Command.Item key={project.id} value={project.id} onSelect={onSelect}>
              {project.name}
            </Command.Item>
          ))}
          <Command.Empty>No matching project</Command.Empty>
        </Command.List>
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-xs text-fg-faint">
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd> add
          </span>
          <span className="flex items-center gap-1">
            <Kbd>esc</Kbd> cancel
          </span>
        </div>
      </Command.Root>
    </div>
  );
}
